import type { HandEvaluator, Rng } from '@/engine/ports';
import { legalActions } from '@/engine/rules';
import type { Action, HandState, LegalAction, PlayerId } from '@/engine/types';
import { computeEquity } from './equity';
import { potOdds, potSize } from './potOdds';
import { enumerateSizings } from './sizing';

export type DecideOptions = {
  rng: Rng;
  evaluator: HandEvaluator;
  /** Monte Carlo trials per decision. Plan G5 default 10_000. */
  trials?: number;
  /** Safety margin on calls: equity must exceed potOdds × (1 + margin). Default 0.05. */
  callMargin?: number;
  /** Precomputed legal actions — when the caller (solver / useBotDriver) has
   *  already derived them, avoids recomputing via `state.players.filter`. */
  legals?: ReadonlyArray<LegalAction>;
};

/**
 * Convert a LegalAction to a concrete Action for `playerId`.
 */
function materialize(legal: LegalAction, playerId: PlayerId): Action {
  switch (legal.type) {
    case 'fold':
      return { type: 'fold', playerId };
    case 'check':
      return { type: 'check', playerId };
    case 'call':
      return { type: 'call', playerId, amount: legal.amount ?? 0 };
    case 'bet':
      return { type: 'bet', playerId, amount: legal.min ?? 0 };
    case 'raise':
      return { type: 'raise', playerId, to: legal.min ?? 0 };
    case 'allin':
      return { type: 'allin', playerId, amount: legal.amount ?? 0 };
  }
}

function activeOpponents(state: HandState, playerId: PlayerId): number {
  return state.players.filter((p) => !p.folded && !p.eliminated && p.id !== playerId).length;
}

/**
 * Pick a postflop action by EV-max reasoning:
 * 1. Estimate hero equity vs remaining active villains (uniform range).
 * 2. If facing a bet, call iff `equity > potOdds × (1 + callMargin)`; else fold.
 * 3. When check/bet is allowed, pick the largest sizing preset that
 *    `equity` justifies (equity > 0.6 → pot, 0.75 → 1.5pot, 0.9 → allin).
 */
export function decidePostflop(
  state: HandState,
  playerId: PlayerId,
  options: DecideOptions,
): Action {
  const legals = options.legals ?? legalActions(state, playerId);
  if (legals.length === 0) {
    throw new Error(`decidePostflop: no legal actions for ${playerId}`);
  }
  const player = state.players.find((p) => p.id === playerId);
  if (!player?.holeCards) {
    throw new Error(`decidePostflop: ${playerId} has no hole cards`);
  }
  const numVillains = Math.max(1, activeOpponents(state, playerId));
  const equity = computeEquity(player.holeCards, state.board, numVillains, {
    trials: options.trials ?? 10_000,
    rng: options.rng,
    evaluator: options.evaluator,
  }).equity;

  const facingBet = state.betting.currentBet > player.streetContribution;
  const margin = options.callMargin ?? 0.05;

  if (facingBet) {
    const foldLegal = legals.find((l) => l.type === 'fold');
    const callLegal = legals.find((l) => l.type === 'call');
    const required = potOdds(state, playerId) * (1 + margin);
    if (equity < required && foldLegal) return materialize(foldLegal, playerId);
    if (!callLegal && foldLegal) return materialize(foldLegal, playerId);
    // Value-raise threshold tightens the more the bet has already escalated
    // this street. Without this, heads-up bot-vs-bot pairs with matching
    // medium-strong equity (0.70–0.85 each) re-raised each other pot-sized
    // on every turn until somebody hit all-in — effectively an infinite
    // raise loop visible in live play. A 3-bet / 4-bet spot now demands
    // near-nuts equity before firing a raise back.
    const raiseEscalation = state.betting.currentBet / Math.max(1, state.level.bb);
    const raiseThreshold = raiseEscalation > 6 ? 0.92 : raiseEscalation > 3 ? 0.85 : 0.7;
    if (equity > raiseThreshold) {
      const sizings = enumerateSizings(state, playerId);
      if (equity > 0.92) {
        const allin = sizings.find((x) => x.label === 'allin');
        if (allin) return materialize(allin.legal, playerId);
      }
      const raise = sizings.find((x) => x.label === 'pot' || x.label === '2/3');
      if (raise) return materialize(raise.legal, playerId);
    }
    if (callLegal) return materialize(callLegal, playerId);
    // No call available means hero already has `streetContribution >= currentBet`
    // (e.g. blind + checked around). Check if possible, otherwise fall back.
    const checkLegal = legals.find((l) => l.type === 'check');
    if (checkLegal) return materialize(checkLegal, playerId);
    return materialize(foldLegal as LegalAction, playerId);
  }

  // No bet outstanding.
  const sizings = enumerateSizings(state, playerId);
  const pot = potSize(state);
  if (pot <= 0) {
    const check = legals.find((l) => l.type === 'check');
    return materialize(check as LegalAction, playerId);
  }
  if (equity > 0.9) {
    const allin = sizings.find((x) => x.label === 'allin');
    if (allin) return materialize(allin.legal, playerId);
  }
  if (equity > 0.75) {
    const s = sizings.find((x) => x.label === 'pot' || x.label === '1.5pot');
    if (s) return materialize(s.legal, playerId);
  }
  if (equity > 0.55) {
    const s = sizings.find((x) => x.label === '1/2' || x.label === '2/3');
    if (s) return materialize(s.legal, playerId);
  }
  if (equity > 0.4) {
    const s = sizings.find((x) => x.label === '1/3');
    if (s) return materialize(s.legal, playerId);
  }
  const check = legals.find((l) => l.type === 'check');
  return check ? materialize(check, playerId) : materialize(legals[0] as LegalAction, playerId);
}
